// src/components/modals/ChangeEmailDialog.tsx
import {
  Alert,
  Button,
  Drawer,
  Field,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  Portal,
  VStack,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { changeEmail } from '@/api/settings.api'
import { SettingsInput } from '@/pages/settings/SettingsControls'

interface ChangeEmailProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  currentEmail: string
  onSuccess?: (newEmail: string) => void

  // Storybook mocks
  mockSubmitting?: boolean
  mockErrorMessage?: string
  mockInitialValues?: {
    currentEmail: string
    email: string
    repeatEmail: string
  }
}

const schema = Yup.object({
  currentEmail: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .required('Dieses Feld ist erforderlich.'),
  email: Yup.string().email('Ungültige E-Mail-Adresse').required('Dieses Feld ist erforderlich.'),
  repeatEmail: Yup.string()
    .oneOf([Yup.ref('email')], 'E-Mail-Adressen stimmen nicht überein.')
    .required('Dieses Feld ist erforderlich.'),
})

export function ChangeEmailDialog(props: ChangeEmailProps) {
  const {
    isOpen,
    onOpenChange,
    currentEmail,
    onSuccess,
    mockSubmitting,
    mockErrorMessage,
    mockInitialValues,
  } = props

  const [submitting, setSubmitting] = useState(false)

  const formik = useFormik({
    initialValues: mockInitialValues || {
      currentEmail: '',
      email: '',
      repeatEmail: '',
    },
    validationSchema: schema,
    validateOnMount: true,
    onSubmit: async values => {
      if (values.currentEmail.trim().toLowerCase() !== (currentEmail || '').trim().toLowerCase()) {
        formik.setFieldError(
          'currentEmail',
          'Bitte geben Sie Ihre aktuelle E-Mail-Adresse korrekt ein.'
        )
        return
      }

      if (values.email.trim().toLowerCase() === (currentEmail || '').trim().toLowerCase()) {
        formik.setFieldError('email', 'Neue E-Mail muss sich von der alten unterscheiden.')
        return
      }

      setSubmitting(true)
      try {
        await changeEmail(values.email)

        toast.success(
          'Bestätigungs-E-Mail wurde versandt',
          'Wir haben Ihnen eine E-Mail zur Bestätigung Ihrer neuen Adresse zugesandt.'
        )

        onOpenChange(false)
        onSuccess?.(values.email)
        formik.resetForm()
      } catch (err: any) {
        if (err?.code === 'USER_EXISTS') {
          formik.setFieldError('email', 'Diese E-Mail-Adresse wird bereits verwendet.')
        } else if (err?.message) {
          formik.setStatus(err.message)
        } else {
          formik.setStatus('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.')
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (mockErrorMessage) formik.setStatus(mockErrorMessage)
  }, [mockErrorMessage])

  const isSubmitting = submitting || mockSubmitting

  function handleClose() {
    onOpenChange(false)
    formik.resetForm()
    formik.setStatus(undefined)
  }

  return (
    <Drawer.Root
      open={isOpen}
      size="xl"
      onOpenChange={e => {
        if (!e.open) handleClose()
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            {/* HEADER */}
            <Drawer.Header>
              <Drawer.Title>E-Mail-Adresse aktualisieren</Drawer.Title>
            </Drawer.Header>

            {/* BODY */}
            <Drawer.Body>
              <fieldset disabled={isSubmitting} style={{ width: '100%' }}>
                <VStack gap={4} align="stretch">
                  {/* CURRENT EMAIL */}
                  <FieldRoot
                    invalid={!!formik.errors.currentEmail && !!formik.touched.currentEmail}
                  >
                    <FieldLabel>
                      Aktuelle E-Mail-Adresse <Field.RequiredIndicator />
                    </FieldLabel>
                    <SettingsInput
                      name="currentEmail"
                      placeholder={currentEmail || 'aktuelle@mail.de'}
                      value={formik.values.currentEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <FieldErrorText>
                      {formik.touched.currentEmail && (formik.errors.currentEmail as any)}
                    </FieldErrorText>
                  </FieldRoot>

                  {/* NEW + REPEAT */}
                  <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                    <FieldRoot flex="1" invalid={!!formik.errors.email && !!formik.touched.email}>
                      <FieldLabel>
                        Neue E-Mail-Adresse <Field.RequiredIndicator />
                      </FieldLabel>
                      <SettingsInput
                        name="email"
                        placeholder="neuemail@mail.de"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FieldErrorText>
                        {formik.touched.email && (formik.errors.email as any)}
                      </FieldErrorText>
                    </FieldRoot>

                    <FieldRoot
                      flex="1"
                      invalid={!!formik.errors.repeatEmail && !!formik.touched.repeatEmail}
                    >
                      <FieldLabel>
                        Neue E-Mail-Adresse wiederholen <Field.RequiredIndicator />
                      </FieldLabel>
                      <SettingsInput
                        name="repeatEmail"
                        placeholder="neuemail@mail.de"
                        value={formik.values.repeatEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FieldErrorText>
                        {formik.touched.repeatEmail && (formik.errors.repeatEmail as any)}
                      </FieldErrorText>
                    </FieldRoot>
                  </Flex>

                  {formik.status && (
                    <Alert.Root status="error">
                      <Alert.Indicator />
                      <Alert.Description>{formik.status}</Alert.Description>
                    </Alert.Root>
                  )}
                </VStack>
              </fieldset>
            </Drawer.Body>

            {/* FOOTER */}
            <Drawer.Footer>
              <Button variant="ghost" onClick={handleClose}>
                Abbrechen
              </Button>

              <Button
                variant="subtle"
                colorPalette="green"
                loading={isSubmitting}
                disabled={!formik.isValid}
                onClick={() => formik.handleSubmit()}
              >
                Neue E-Mail-Adresse speichern
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
